// Browser-side Supabase client. Use inside 'use client' components.
//
// Strict client-only: the underlying createBrowserClient touches `document`
// and `window` at construction time. Calling it during SSG/SSR crashes the
// prerender. We guard with `typeof window` and return a noop stub server-side.
//
// Client code that imports `{ supabase }` and uses it inside effects/handlers
// (the standard pattern) keeps working. Code that uses `supabase.*` at the top
// of a module body needs to be moved into an effect — but the existing codebase
// already does this correctly.

import { createBrowserClient } from "@supabase/ssr";
import type { Database } from "@/integrations/supabase/types";
import type { SupabaseClient } from "@supabase/supabase-js";

const SUPABASE_URL = "https://alyidbbieegylgvdqmis.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFseWlkYmJpZWVneWxndmRxbWlzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjAxNTg2MzIsImV4cCI6MjA3NTczNDYzMn0.uCnn2HpE9_peSiLAyTmpoKWRKMPTPs-bE_sR3Mo5c24";

let cached: SupabaseClient<Database> | null = null;

export function createClient(): SupabaseClient<Database> {
  if (typeof window === "undefined") {
    // Server-side guard: shouldn't happen in correctly-written client components,
    // but the prerender does evaluate module-level code. Return a tagged stub
    // that throws on any property access, which surfaces bad call-sites loudly.
    return new Proxy({} as SupabaseClient<Database>, {
      get(_t, prop) {
        if (prop === Symbol.toPrimitive || prop === "then") return undefined;
        // Return a no-op chainable for nested access during SSR so prerendering
        // doesn't crash on `supabase.auth.X` happening inside `useEffect` bodies
        // that the prerender evaluates lazily.
        return new Proxy(() => undefined, {
          get() {
            return () => undefined;
          },
          apply() {
            return Promise.resolve({ data: null, error: null });
          },
        });
      },
    });
  }
  if (cached) return cached;
  // Adapt SSR 0.5 declarations to the installed SDK generic parameter order.
  cached = createBrowserClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY) as unknown as SupabaseClient<Database>;
  return cached;
}

// Convenience singleton — same lazy semantics.
export const supabase: SupabaseClient<Database> = new Proxy(
  {} as SupabaseClient<Database>,
  {
    get(_t, prop) {
      const client = createClient();
      const value = client[prop];
      return typeof value === "function" ? value.bind(client) : value;
    },
  }
);
